import {
    assetsTypes as contentTypes,
    defaultAnalyticsConsent,
    defaultProfile,
    desktopKeyword,
    mobileKeyword,
} from '@/shared/constants/appConstants'
import { createWithEqualityFn } from 'zustand/traditional'
import { createJSONStorage, persist } from 'zustand/middleware'
import { persistStorage } from '@/shared/state/dataBase/persistStorage'

import assetsTypes from '../../types/assetsTypes/assetsTypes'
import { Skin, TermsAndConditions } from '@/types/ConfigProps'
import { ConsentSettings } from 'firebase/analytics'

export interface Profile {
    iconId: string
    id: string
    isKid: boolean
    order: string
    profile_name: string
    version: number
    default?: boolean
}

export interface AppStore {
    isCookiesAccepted: boolean
    isCacheChecked: boolean
    userEmail: string
    isUserLoggedIn: boolean
    isGuest: boolean
    devicesLimitExceeded: boolean
    previousPage: string
    appSkin: Skin | null
    profiles: Profile[]
    actualProfile: Profile | null
    device: string
    assetType: assetsTypes
    termsAndConditions: TermsAndConditions
    faq: string
    tAndC: string
    analyticsConfigConsent: ConsentSettings

    setIsUserLoggedIn: (isUserLoggedIn: boolean) => void
    setIsGuest: (isGuest: boolean) => void
    setDevicesLimitExceeded: (hasExceededLimit: boolean) => void
    setPreviousPage: (page: string) => void
    setAssetType: (type: assetsTypes) => void
    setAppSkin: (newSkin: Skin) => void
    setProfiles: (profiles: Profile[]) => void
    setTermsAndConditions: (termsAndConditions: TermsAndConditions) => void
    setActualProfile: (actualProfile: Profile) => void
    setFaq: (faq: string) => void
    setTAndC: (value: string) => void
    setEmail: (value: string) => void
    setIsCacheChecked: (value: boolean) => void
    setIsCookiesAccepted: (value: boolean) => void
    clearAppStorage: () => void
    setAnalyticsConfigConsent: (value: ConsentSettings) => void
    resetToDefaults: () => void
}

export const useAppStore = createWithEqualityFn<AppStore>()(
    persist(
        (set) => ({
            isCacheChecked: false,
            isCookiesAccepted: false,
            userEmail: '',
            isUserLoggedIn: false,
            isGuest: false,
            devicesLimitExceeded: false,
            previousPage: '/',
            profiles: [],
            actualProfile: defaultProfile,
            device: navigator.userAgent.includes(mobileKeyword)
                ? mobileKeyword
                : desktopKeyword,
            assetType: contentTypes.TIVI,
            appSkin: null,
            termsAndConditions: { isTermsAccepted: false, version: 0, url: '' },
            faq: '',
            tAndC: '',
            analyticsConfigConsent: defaultAnalyticsConsent,

            setIsUserLoggedIn: (isUserLoggedIn: boolean) =>
                set(() => ({
                    isUserLoggedIn,
                })),
            setIsGuest: (isGuest: boolean) =>
                set(() => ({
                    isGuest,
                })),
            setDevicesLimitExceeded: (hasExceededLimit: boolean) =>
                set(() => ({
                    devicesLimitExceeded: hasExceededLimit,
                })),
            setPreviousPage: (page: string) =>
                set(() => ({
                    previousPage: page,
                })),
            setAssetType: (type: assetsTypes) =>
                set(() => ({
                    assetType: type,
                })),
            setAppSkin: (newSkin: Skin) =>
                set(() => ({
                    appSkin: newSkin,
                })),
            setProfiles: (profiles: Profile[]) =>
                set(() => ({
                    profiles,
                })),
            setTermsAndConditions: (termsAndConditions: TermsAndConditions) =>
                set(() => ({
                    termsAndConditions,
                })),
            setActualProfile: (actualProfile: Profile) =>
                set(() => ({
                    actualProfile,
                })),
            setFaq: (faq: string) =>
                set(() => ({
                    faq,
                })),
            setTAndC: (value: string) =>
                set(() => ({
                    tAndC: value,
                })),
            setEmail: (value: string) =>
                set(() => ({
                    userEmail: value,
                })),
            setIsCookiesAccepted: (value: boolean) =>
                set(() => ({
                    isCookiesAccepted: value,
                })),
            setIsCacheChecked: (value: boolean) =>
                set(() => ({
                    isCacheChecked: value,
                })),
            clearAppStorage: () =>
                set(() => ({
                    isGuest: false,
                    isCacheChecked: false,
                    isCookiesAccepted: false,
                    userEmail: '',
                    isUserLoggedIn: false,
                    devicesLimitExceeded: false,
                })),
            setAnalyticsConfigConsent: (value: ConsentSettings) =>
                set(() => ({
                    analyticsConfigConsent: value,
                })),
            resetToDefaults: () =>
                set(() => ({
                    isGuest: false,
                    previousPage: '/',
                    profiles: [],
                    actualProfile: defaultProfile,
                    device: navigator.userAgent.includes(mobileKeyword)
                        ? mobileKeyword
                        : desktopKeyword,
                    assetType: contentTypes.TIVI,
                    appSkin: null,
                    termsAndConditions: {
                        isTermsAccepted: false,
                        version: 0,
                        url: '',
                    },
                    faq: '',
                    tAndC: '',
                    devicesLimitExceeded: false,
                })),
        }),
        {
            name: 'app-storage',
            storage: createJSONStorage(() => persistStorage),
        }
    )
)
